<template>
  <div class="container-fluid px-4 mt-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <div>
        <h5 class="fw-bold mb-2">💳 카드 결제 내역</h5>
        <div class="btn-group btn-group-sm" role="group">
          <button type="button" class="btn btn-primary active">결제 내역</button>
          <router-link to="/admin/payment-cancel" class="btn btn-outline-primary">카드 취소 내역</router-link>
          <router-link to="/admin/payment-fail-result" class="btn btn-outline-primary">결제 실패 내역</router-link>
        </div>
      </div>
      <span class="text-muted small">총 {{ pagination.total }}건</span>
    </div>

    <!-- 검색 -->
    <div class="row g-2 mb-3">
      <div class="col-auto">
        <div class="input-group">
          <input 
            v-model="searchCarNum" 
            type="text" 
            class="form-control form-control-sm" 
            placeholder="차량번호 입력 (3444 형식)" 
            style="width: 200px;"
            @keyup.enter="performSearch"
          />
          <button class="btn btn-sm btn-primary" @click="performSearch" :disabled="loading">
            {{ loading ? '검색 중...' : '검색' }}
          </button>
          <button class="btn btn-sm btn-outline-secondary" @click="resetSearch">초기화</button>
        </div>
      </div>
      <div class="col-auto">
        <select v-model="currentLimit" class="form-select form-select-sm" style="width: 100px;">
          <option :value="10">10개</option>
          <option :value="20">20개</option>
          <option :value="50">50개</option>
        </select>
      </div>
    </div>

    <!-- 테이블 -->
    <div class="table-responsive">
      <table class="table table-sm table-hover border">
        <thead class="table-light sticky-top">
          <tr>
            <th class="text-center">ID</th>
            <th class="text-center">Tid</th>
            <th class="text-center">구매자명</th>
            <th class="text-center">차량번호</th>
            <th class="text-center">결제금액</th>
            <th class="text-center">결제수단</th>
            <th class="text-center">결과코드</th>
            <th class="text-center">인증일시</th>
            <th class="text-center">상품명</th>
            <th class="text-center">주차장</th>
            <th class="text-center">연락처</th>
            <th class="text-center">이메일</th>
            <th class="text-center">등록일시</th>
            <th class="text-center">결제상태</th>
            <th class="text-center">작업</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="results.length === 0">
            <td colspan="15" class="text-center text-muted py-4">결제 내역이 없습니다.</td>
          </tr>
          <tr v-for="item in results" :key="item.Id" class="align-middle">
            <td class="text-center small">{{ item.Id }}</td>
            <td class="text-center small">{{ item.Tid || '-' }}</td>
            <td class="text-center">{{ item.BuyerName || '-' }}</td>
            <td class="text-center fw-bold">{{ item.CarNum || '-' }}</td>
            <td class="text-center text-danger fw-bold">{{ item.Amt ? item.Amt.toLocaleString() : '-' }} 원</td>
            <td class="text-center small">{{ item.PayMethod || '-' }}</td>
            <td class="text-center">
              <span :class="{
                'badge bg-success': item.ResultCode === '0000',
                'badge bg-danger': item.ResultCode && item.ResultCode !== '0000',
                'text-muted': !item.ResultCode
              }">
                {{ item.ResultCode || '-' }}
              </span>
            </td>
            <td class="text-center small">{{ item.AuthDate || '-' }}</td>
            <td class="text-center small">{{ item.GoodsName || '-' }}</td>
            <td class="text-center small">{{ item.AreaName || '-' }}</td>
            <td class="text-center small">{{ item.BuyerTel || '-' }}</td>
            <td class="text-center small">{{ item.BuyerEmail || '-' }}</td>
            <td class="text-center small">{{ formatDate(item.RegDate) }}</td>
            <td class="text-center">
              <span :class="{
                'badge bg-info': !item.SETTLEMENT_STATUS,
                'badge bg-warning': item.SETTLEMENT_STATUS === 'pending',
                'badge bg-success': item.SETTLEMENT_STATUS === 'completed'
              }">
                {{ item.SETTLEMENT_STATUS || '결제완료' }}
              </span>
            </td>
            <td class="text-center">
              <button 
                class="btn btn-sm btn-outline-danger" 
                @click="cancelPayment(item)"
                title="결제 취소"
              >
                ❌ 취소
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 페이징 -->
    <nav v-if="pagination.total > 0" aria-label="Page navigation" class="mt-3">
      <ul class="pagination justify-content-center">
        <li :class="{ 'page-item disabled': currentOffset === 0 }">
          <button class="page-link" @click="previousPage">이전</button>
        </li>
        <li class="page-item active">
          <span class="page-link">{{ currentPage }} / {{ totalPages }}</span>
        </li>
        <li :class="{ 'page-item disabled': !pagination.hasMore }">
          <button class="page-link" @click="nextPage" :disabled="!pagination.hasMore">다음</button>
        </li>
      </ul>
    </nav>

    <!-- 취소 결과 모달 -->
    <div class="modal fade" id="cancelResultModal" tabindex="-1" aria-labelledby="cancelResultLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title" id="cancelResultLabel">💳 결제 취소 결과</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="cancelResult">
              <div class="row mb-3">
                <div class="col-md-6">
                  <p><strong>Tid:</strong> <code>{{ cancelResult.Tid || '-' }}</code></p>
                  <p><strong>결제 금액:</strong> {{ cancelResult.CancelAmt ? cancelResult.CancelAmt.toLocaleString() : '-' }} 원</p>
                  <p><strong>결과코드:</strong> <span :class="{ 'badge bg-danger': cancelResult.ResultCode !== '0000', 'badge bg-success': cancelResult.ResultCode === '0000' }">{{ cancelResult.ResultCode || '-' }}</span></p>
                  <p><strong>결과메시지:</strong> {{ cancelResult.ResultMsg || '-' }}</p>
                </div>
                <div class="col-md-6">
                  <p><strong>취소 금액:</strong> {{ cancelResult.CancelAmt ? cancelResult.CancelAmt.toLocaleString() : '-' }} 원</p>
                  <p><strong>취소 번호:</strong> {{ cancelResult.CancelNum || '-' }}</p>
                  <p><strong>에러코드:</strong> {{ cancelResult.ErrorCode || '-' }}</p>
                  <p><strong>에러메시지:</strong> {{ cancelResult.ErrorMsg || '-' }}</p>
                </div>
              </div>
              <hr/>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">닫기</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import axios from 'axios'
import * as bootstrap from 'bootstrap'
import { handleCancelPayment } from '../../utils/payment'

const SERVER_URL = process.env.VUE_APP_API_BASE

const results = ref([])
const loading = ref(false)
const searchCarNum = ref('')
const isSearching = ref(false)
const currentOffset = ref(0)
const currentLimit = ref(20)
const cancelResult = ref(null)
let cancelResultModal = null  // 모달 인스턴스 캐시
const pagination = reactive({
  total: 0,
  limit: 20,
  offset: 0,
  hasMore: false
})

const currentPage = computed(() => Math.floor(currentOffset.value / currentLimit.value) + 1)
const totalPages = computed(() => Math.ceil(pagination.total / currentLimit.value) || 1)

async function fetchPaymentResults() {
  loading.value = true
  try {
    const params = {
      limit: currentLimit.value,
      offset: currentOffset.value
    }

    let url = `${SERVER_URL}/api/admin/payment-result`
    
    if (isSearching.value && searchCarNum.value) {
      url = `${SERVER_URL}/api/admin/payment-result/search`
      params.carNum = searchCarNum.value
    }

    const response = await axios.get(url, { params })
    
    if (response.data.success) {
      results.value = response.data.data || []
      pagination.total = response.data.pagination.total
      pagination.limit = response.data.pagination.limit
      pagination.offset = response.data.pagination.offset
      pagination.hasMore = response.data.pagination.hasMore
      console.log('[PaymentResult] 조회 완료:', results.value.length, '건')
    } else {
      alert(response.data.message || '조회에 실패했습니다.')
    }
  } catch (e) {
    console.error('[PaymentResult] 조회 오류:', e)
    alert('조회 중 오류가 발생했습니다.')
  } finally {
    loading.value = false
  }
}

function performSearch() {
  if (!searchCarNum.value.trim()) {
    alert('차량번호를 입력하세요.')
    return
  }
  isSearching.value = true
  currentOffset.value = 0
  fetchPaymentResults()
}

function resetSearch() {
  searchCarNum.value = ''
  isSearching.value = false
  currentOffset.value = 0
  fetchPaymentResults()
}

function previousPage() {
  if (currentOffset.value >= currentLimit.value) {
    currentOffset.value -= currentLimit.value
    fetchPaymentResults()
  }
}

function nextPage() {
  if (pagination.hasMore) {
    currentOffset.value += currentLimit.value
    fetchPaymentResults()
  }
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  try {
    const date = new Date(dateStr)
    return date.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
  } catch (e) {
    return dateStr
  }
}

function showCancelResultModal() {
  const modalEl = document.getElementById('cancelResultModal');
  if (!modalEl) {
    console.error('[showCancelResultModal] 모달 요소를 찾을 수 없음');
    return;
  }
  
  if (!cancelResultModal) {
    cancelResultModal = new bootstrap.Modal(modalEl, { backdrop: 'static', keyboard: false });
  }
  
  cancelResultModal.show();
  console.log('[showCancelResultModal] 모달 표시됨');
}

async function cancelPayment(item) {
  if (!item || !item.Tid) {
    alert('Tid가 없는 결제 정보입니다. 취소할 수 없습니다.');
    return;
  }

  loading.value = true;

  try {
    const result = await handleCancelPayment({
      Tid: item.Tid,
      Amt: item.Amt,
      AreaSeq: item.AreaSeq || '',
      CarNum: item.CarNum || '',
      GoodsName: item.GoodsName || ''
    });

    if (result) {
      cancelResult.value = result;
      showCancelResultModal();
    }
  } finally {
    loading.value = false;
    fetchPaymentResults();
  }
}

onMounted(() => {
  fetchPaymentResults()
})
</script>

<style scoped>
.table {
  font-size: 0.85rem;
}

.table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #dee2e6;
}

.table-hover tbody tr:hover {
  background-color: #f5f5f5;
}

.badge {
  font-weight: 500;
  padding: 4px 8px;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 0.8rem;
}
</style>
